import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => nav("/login"), 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-6">
      <div className="max-w-md w-full border co-hairline bg-white p-8 lg:p-10">
        {!token ? (
          <>
            <div className="co-label text-[#B91C1C]">Missing token</div>
            <h1 className="font-heading text-xl font-bold tracking-tight mt-2">
              This reset link is invalid
            </h1>
            <p className="text-sm text-[#52525B] mt-2">
              Request a new one from the login screen.
            </p>
            <Link to="/forgot-password" className="mt-4 inline-block text-sm text-[#4338CA] font-semibold">
              Request a new link
            </Link>
          </>
        ) : done ? (
          <div className="space-y-4" data-testid="reset-done">
            <div className="w-12 h-12 grid place-items-center rounded-sm bg-[#DCFCE7] text-[#15803D]">
              <CheckCircle2 className="h-6 w-6" strokeWidth={1.75} />
            </div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">
              Password updated
            </h1>
            <p className="text-sm text-[#52525B]">Taking you to login…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <div className="w-12 h-12 grid place-items-center rounded-sm bg-[#ECECFE] text-[#4338CA]">
              <KeyRound className="h-6 w-6" strokeWidth={1.75} />
            </div>
            <div className="co-label">Reset password</div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">
              Set a new password
            </h1>
            <input
              type="password"
              required
              autoFocus
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="New password (min 8 chars)"
              data-testid="reset-new-input"
              className="w-full border co-hairline px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#4338CA]"
            />
            <input
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm new password"
              data-testid="reset-confirm-input"
              className="w-full border co-hairline px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#4338CA]"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="reset-submit"
              className="w-full bg-[#4338CA] hover:bg-[#3730A3] text-white px-4 py-2.5 text-sm font-semibold rounded-sm co-btn-press disabled:opacity-60 transition-colors duration-200"
            >
              {busy ? "Saving…" : "Set new password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
